import { apiClient } from '../api/client';

/**
 * Get all disputes for the current user
 * @returns {Promise<Array>} Array of dispute objects
 */
export const getUserDisputes = async () => {
  try {
    return await apiClient.disputes.getAll();
  } catch (error) {
    console.error('Error fetching user disputes:', error);
    throw error;
  }
};

/**
 * Get dispute by ID
 * @param {string} disputeId - The ID of the dispute to fetch
 * @returns {Promise<Object>} Dispute object with messages
 */
export const getDisputeById = async (disputeId: string) => {
  try {
    return await apiClient.disputes.getById(disputeId);
  } catch (error) {
    console.error(`Error fetching dispute ${disputeId}:`, error);
    throw error;
  }
};

/**
 * Get dispute by transaction ID
 * @param {string} transactionId - The ID of the transaction
 * @returns {Promise<Object>} Dispute object if exists, null otherwise
 */
export const getDisputeByTransactionId = async (transactionId: string) => {
  try {
    return await apiClient.disputes.getByTransactionId(transactionId);
  } catch (error: any) {
    if (error.response && error.response.status === 404) {
      // No dispute found for this transaction
      return null;
    }
    console.error(`Error fetching dispute for transaction ${transactionId}:`, error);
    throw error;
  }
};

/**
 * Create a new dispute
 * @param {Object} disputeData - The dispute data
 * @param {string} disputeData.transactionId - The ID of the transaction to dispute
 * @param {string} disputeData.reason - The reason for the dispute
 * @param {string} [disputeData.message] - Optional initial message
 * @returns {Promise<Object>} Created dispute object
 */
export const createDispute = async (disputeData: { transactionId: string; reason: string; message?: string }) => {
  try {
    return await apiClient.disputes.create(disputeData);
  } catch (error) {
    console.error('Error creating dispute:', error);
    throw error;
  }
};

/**
 * Add a message to a dispute
 * @param {string} disputeId - The ID of the dispute
 * @param {Object} messageData - The message data
 * @param {string} messageData.message - The message content
 * @param {string} [messageData.visibility] - Optional visibility setting (admin only)
 * @returns {Promise<Object>} Created message object
 */
export const addDisputeMessage = async (disputeId: string, messageData: { message: string; visibility?: string }) => {
  try {
    return await apiClient.disputes.addMessage(disputeId, messageData);
  } catch (error) {
    console.error(`Error adding message to dispute ${disputeId}:`, error);
    throw error;
  }
};

/**
 * Resolve a dispute (admin only)
 * @param {string} disputeId - The ID of the dispute
 * @param {Object} resolutionData - The resolution data
 * @param {string} resolutionData.resolution - The resolution type ('refund_to_buyer' or 'release_to_seller')
 * @param {string} [resolutionData.notes] - Optional resolution notes
 * @returns {Promise<Object>} Updated dispute object
 */
export const resolveDispute = async (disputeId: string, resolutionData: { resolution: string; notes?: string }) => {
  try {
    return await apiClient.disputes.resolve(disputeId, resolutionData);
  } catch (error) {
    console.error(`Error resolving dispute ${disputeId}:`, error);
    throw error;
  }
};

/**
 * Get dispute statistics (admin only)
 * @returns {Promise<Object>} Dispute statistics
 */
export const getDisputeStats = async () => {
  try {
    return await apiClient.disputes.getStats();
  } catch (error) {
    console.error('Error fetching dispute statistics:', error);
    throw error;
  }
};

/**
 * Check if user is admin
 * @returns {Promise<boolean>} True if user is admin, false otherwise
 */
export const checkAdminStatus = async () => {
  try {
    const user = await apiClient.auth.getCurrentUser() as any;
    return user && user.isAdmin || false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};